// Scalar string funcs — `concat`, `upper`, `lower`, `substring`,
// `length`, `trim`, `contains`, `starts-with`, `ends-with`, `replace`,
// `split`, `index-of`. Registered in `opMeta` (NOT a verb registry),
// so they are usable in any expression context: derive bodies, filter
// predicates, top-level bindings. Composed into `transformOps` so the
// generated LLM DSL reference (`canonicalDslScope`) surfaces them.
//
// Null convention (pinned): a null/undefined SUBJECT (first arg)
// short-circuits — `null` for value-returning ops, `false` for the
// predicate-shaped ops (contains/starts-with/ends-with), `-1` for
// `index-of`. `concat` is the exception: it is forgiving (built for
// data-cleaning) and coerces null args to `""`.

import { z } from 'zod';

import { defineFold } from '../../builtins.ts';
import { opMeta } from '../../op-meta.ts';
import { booleanCol, type ColumnType, numberCol, stringCol, unknownCol } from '../../schema/types.ts';
import type { Value } from '../../value.ts';

/**
 * Build a null-propagating string op. The first positional is the
 * SUBJECT: a null/undefined subject short-circuits to `whenNull`
 * (default `null`); otherwise the subject is `String()`-coerced and
 * the remaining raw args are handed to `fn`. Built on the shared
 * `defineFold` so the apply path (schema-resolved params) and the
 * compileExpr path (per-row values) share ONE reducer — no per-op
 * apply/compileExpr duplication. Schemas use `z.unknown()` params so
 * a null survives to the reducer instead of being coerced away.
 */
const subjectOp = (
  fn: (subject: string, rest: readonly unknown[]) => Value,
  resultType: ColumnType,
  whenNull: Value = null,
) =>
  defineFold<unknown>(
    (vs) => {
      const [subject, ...rest] = vs;
      return subject === null || subject === undefined ? whenNull : fn(String(subject), rest);
    },
    undefined,
    resultType,
  );

export const concat = z
  .object({ name: z.literal('concat'), params: z.array(z.unknown()).min(1) })
  .describe(
    'Concatenate 1+ values into a string. Null/undefined args are treated as "" (forgiving — built for data-cleaning).',
  )
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(concat "a" "b" "c")', result: '"abc"' },
    ...defineFold<unknown>(
      (vs) => vs.map((v) => (v === null || v === undefined ? '' : String(v))).join(''),
      undefined,
      stringCol,
    ),
  });

export const upper = z
  .object({ name: z.literal('upper'), params: z.tuple([z.unknown()]) })
  .describe('Uppercase a string. Null ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(upper "ab")', result: '"AB"' },
    ...subjectOp((s) => s.toUpperCase(), stringCol),
  });

export const lower = z
  .object({ name: z.literal('lower'), params: z.tuple([z.unknown()]) })
  .describe('Lowercase a string. Null ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(lower "AB")', result: '"ab"' },
    ...subjectOp((s) => s.toLowerCase(), stringCol),
  });

export const trim = z
  .object({ name: z.literal('trim'), params: z.tuple([z.unknown()]) })
  .describe('Strip leading/trailing whitespace. Null ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(trim "  hi  ")', result: '"hi"' },
    ...subjectOp((s) => s.trim(), stringCol),
  });

export const length = z
  .object({ name: z.literal('length'), params: z.tuple([z.unknown()]) })
  .describe('String length. Null ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(length "abcd")', result: '4' },
    ...subjectOp((s) => s.length, numberCol),
  });

export const substring = z
  .object({ name: z.literal('substring'), params: z.array(z.unknown()).min(2).max(3) })
  .describe('(substring s start [end]) — JS .substring semantics (end-exclusive, optional). Null subject ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(substring "hello" 1 3)', result: '"el"' },
    ...subjectOp(
      (s, rest) =>
        rest[1] === undefined ? s.substring(Number(rest[0])) : s.substring(Number(rest[0]), Number(rest[1])),
      stringCol,
    ),
  });

export const replace = z
  .object({ name: z.literal('replace'), params: z.tuple([z.unknown(), z.unknown(), z.unknown()]) })
  .describe('(replace s from to) — replace ALL literal occurrences (not a regex). Null subject ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(replace "a-b-c" "-" "_")', result: '"a_b_c"' },
    ...subjectOp((s, rest) => s.split(String(rest[0])).join(String(rest[1])), stringCol),
  });

export const contains = z
  .object({ name: z.literal('contains'), params: z.tuple([z.unknown(), z.unknown()]) })
  .describe('(contains s sub) — true iff s contains sub. Null subject ⇒ false.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(contains "hello" "ell")', result: 'true' },
    ...subjectOp((s, rest) => s.includes(String(rest[0])), booleanCol, false),
  });

export const startsWith = z
  .object({ name: z.literal('starts-with'), params: z.tuple([z.unknown(), z.unknown()]) })
  .describe('(starts-with s prefix) — Null subject ⇒ false.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(starts-with "hello" "he")', result: 'true' },
    ...subjectOp((s, rest) => s.startsWith(String(rest[0])), booleanCol, false),
  });

export const endsWith = z
  .object({ name: z.literal('ends-with'), params: z.tuple([z.unknown(), z.unknown()]) })
  .describe('(ends-with s suffix) — Null subject ⇒ false.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(ends-with "hello" "lo")', result: 'true' },
    ...subjectOp((s, rest) => s.endsWith(String(rest[0])), booleanCol, false),
  });

export const indexOf = z
  .object({ name: z.literal('index-of'), params: z.tuple([z.unknown(), z.unknown()]) })
  .describe('(index-of s sub) — 0-based index of first occurrence, or -1. Null subject ⇒ -1.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(index-of "hello" "l")', result: '2' },
    ...subjectOp((s, rest) => s.indexOf(String(rest[0])), numberCol, -1),
  });

export const split = z
  .object({ name: z.literal('split'), params: z.tuple([z.unknown(), z.unknown()]) })
  .describe('(split s sep) — split into an array of substrings. Null subject ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(split "a,b,c" ",")', result: '["a","b","c"]' },
    ...subjectOp((s, rest) => s.split(String(rest[0])), unknownCol),
  });

/** All scalar string-func schemas — composed into `transformOps`. */
export const stringFuncs = [
  concat,
  upper,
  lower,
  trim,
  length,
  substring,
  replace,
  contains,
  startsWith,
  endsWith,
  indexOf,
  split,
] as const;
